/**
 * Scoped CSS for the MyCIO landing page. Holds what inline-style objects can't: keyframes,
 * `:hover`, and the four responsive tiers. The exact px overrides come from the prototype's
 * `<style>` media blocks (MyCIO Landing.dc.html). All rules are namespaced under `.lp` so they
 * never leak into the rest of the app.
 *
 * Tiers (handoff): Phone ≤640 · Tablet 641–1024 · Laptop 1025–1440 (the 1180px default) ·
 * Desktop ≥1441 (1320px) and ≥1700 (1440px). H1: 44 / 62 / 78 / 88.
 */
export const LANDING_CSS = `
.lp { font-family: var(--font-hanken), 'Hanken Grotesk', system-ui, sans-serif; }

/* ===== keyframes ===== */
@keyframes mc_marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes mc_fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
@keyframes mc_glide { from { left: 4%; } to { left: 96%; } }
@keyframes mc_heroFadeOut {
  0% { opacity: 1; filter: blur(0); transform: none; }
  100% { opacity: 0; filter: blur(10px); transform: translateY(-12px); }
}
@keyframes mc_heroDissolve {
  0% { opacity: 0; filter: blur(10px); transform: translateY(12px); }
  100% { opacity: 1; filter: blur(0); transform: none; }
}

/* Hero cross-dissolve phases (driven by React state). */
.lp .mc-hero-out { animation: mc_heroFadeOut 0.74s cubic-bezier(0.4,0,1,1) both; }
.lp .mc-hero-in { animation: mc_heroDissolve 1.5s cubic-bezier(0.22,1,0.36,1) both; }
.lp .mc-hero-lead-in { animation: mc_heroDissolve 1.5s cubic-bezier(0.22,1,0.36,1) both; animation-delay: 0.1s; }
.lp .mc-hero-lead-out { animation: mc_heroFadeOut 0.74s cubic-bezier(0.4,0,1,1) both; animation-delay: 0.1s; }

.lp .mc-fadeup { animation: mc_fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both; }
.lp .mc-marquee-track { display: flex; width: max-content; white-space: nowrap; animation: mc_marquee 36s linear infinite; }
.lp .mc-marquee:hover .mc-marquee-track { animation-play-state: paused; }
.lp .mc-glide-dot { animation: mc_glide 6.5s ease-in-out infinite alternate; }

/* Reveal-on-scroll: start hidden, .is-in lifts. */
.lp [data-reveal] { opacity: 0; transform: translateY(14px); transition: opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1); will-change: opacity, transform; }
.lp [data-reveal].is-in { opacity: 1; transform: none; }

/* ===== hover rules (can't be inline) ===== */
.lp .mc-navlink, .lp .mc-navlink-keep { transition: color 0.2s ease; }
.lp .mc-navlink:hover, .lp .mc-navlink-keep:hover { color: #23211C; }
.lp .mc-cta-primary { transition: background 0.2s ease; }
.lp .mc-cta-primary:hover { background: #21463A; }
.lp .mc-cta-secondary { transition: border-color 0.2s ease; }
.lp .mc-cta-secondary:hover { border-color: #23211C; }
.lp .mc-cta-light { transition: background 0.2s ease; }
.lp .mc-cta-light:hover { background: #FFFFFF; }
.lp .mc-link-underline:hover { text-decoration: underline; }
.lp .mc-foot-link { transition: color 0.2s ease; }
.lp .mc-foot-link:hover { color: #FFFFFF; }
.lp .mc-chip { transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease; }
.lp .mc-chip:hover { transform: translateY(-2px); border-color: #2F5D4F; }
/* ===== STAGGER (handoff: data-stagger on a revealed grid) =====
   Children of a revealed [data-stagger] container arrive one after another instead of as a block —
   the difference between a page that "loads" and one that composes itself. Pure CSS: the reveal
   observer already toggles .is-in on the container; each child just waits its turn. Capped at the
   eighth child so a long list never makes the tail feel late. */
.lp [data-stagger] > * { opacity: 0; transform: translateY(12px); transition: opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1); }
.lp [data-stagger].is-in > * { opacity: 1; transform: none; }
.lp [data-stagger].is-in > :nth-child(2) { transition-delay: 0.07s; }
.lp [data-stagger].is-in > :nth-child(3) { transition-delay: 0.14s; }
.lp [data-stagger].is-in > :nth-child(4) { transition-delay: 0.21s; }
.lp [data-stagger].is-in > :nth-child(5) { transition-delay: 0.28s; }
.lp [data-stagger].is-in > :nth-child(6) { transition-delay: 0.35s; }
.lp [data-stagger].is-in > :nth-child(7) { transition-delay: 0.42s; }
.lp [data-stagger].is-in > :nth-child(n+8) { transition-delay: 0.49s; }

/* ===== CARD LIFT — the same gesture the pricing cards already make, on the connect cards that lacked it ===== */
.lp .mc-connectcard { transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s cubic-bezier(0.22,1,0.36,1); }
.lp .mc-connectcard:hover { transform: translateY(-3px); box-shadow: 0 18px 40px -26px rgba(35,33,28,0.45); }

.lp .mc-flagchip { transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease; }
.lp .mc-flagchip:hover { transform: translateY(-2px); border-color: #C9B79B; box-shadow: 0 6px 16px -10px rgba(35,33,28,0.4); }
.lp .mc-state { transition: background-color 0.2s ease, border-color 0.2s ease; }
.lp .mc-state:hover { background-color: #E6EDE7; border-color: #B7C8BD; }
.lp .mc-pricecard { transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s cubic-bezier(0.22,1,0.36,1); }
.lp .mc-pricecard:hover { transform: translateY(-5px); box-shadow: 0 20px 44px -28px rgba(35,33,28,0.45); }
.lp .mc-langbtn { transition: border-color 0.2s ease; }
.lp .mc-langbtn:hover { border-color: #23211C; }
/* Coverage flag chips link to the country pages; the tier pill inside them is a band-style pill. */
.lp .mc-flagchip { text-decoration: none; }
.lp .mc-link-plain { text-decoration: none; }
.lp .mc-link-plain:hover { text-decoration: underline; }
/* Sticky header: the design's 0.82 bone + 12px blur (LandingPage no longer needs a fixed-header spacer). */
.lp .mc-nav { position: sticky; top: 0; z-index: 50; background: rgba(246,243,236,0.82); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border-bottom: 1px solid #DED7C7; }
.lp :where(a,button,input,summary):focus-visible { outline: 3px solid #916B2C; outline-offset: 4px; }
/* Case shelf: a slim always-visible rail, because a scroll region whose scrollbar only appears on
   hover reads as a clipped row on a trackpad and the cases past the fold are never found. */
.lp .mc-case-shelf { scrollbar-width: thin; scrollbar-color: #C9B79B transparent; -webkit-overflow-scrolling: touch; }
.lp .mc-case-shelf::-webkit-scrollbar { height: 8px; }
.lp .mc-case-shelf::-webkit-scrollbar-thumb { background: #C9B79B; border-radius: 999px; }
.lp .mc-case-shelf::-webkit-scrollbar-track { background: transparent; }
.lp .mc-case-shelf > button { transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s cubic-bezier(0.22,1,0.36,1), border-color 0.2s ease; }
.lp .mc-case-shelf > button:hover { transform: translateY(-4px); border-color: #C9B79B; box-shadow: 0 24px 50px -28px rgba(35,33,28,0.5); }
@media (prefers-reduced-motion: reduce) {
  .lp .mc-case-shelf > button, .lp .mc-case-shelf > button:hover { transition: none; transform: none; }
}

/* ===== Phone (≤640px) ===== */
@media (max-width: 640px) {
  .lp .mc-h1 { font-size: 44px !important; }
  .lp .mc-h2 { font-size: 30px !important; line-height: 1.12 !important; }
  .lp .mc-navlink, .lp .mc-lang { display: none !important; }
  .lp .mc-container { padding-left: 20px !important; padding-right: 20px !important; }
  .lp .mc-pad-hero { padding: 40px 20px 44px !important; }
  .lp .mc-pad-section { padding: 60px 20px !important; }
  .lp .mc-pad-faq { padding: 60px 20px !important; }
  .lp .mc-pad-finalcta { padding: 64px 20px !important; }
  .lp .mc-gap64 { gap: 36px !important; }
  .lp .mc-gap48 { gap: 28px !important; }
  .lp .mc-finalcta-h { font-size: 38px !important; }
  .lp .mc-nav-inner { padding-left: 20px !important; padding-right: 20px !important; }
  .lp .mc-nav-actions { gap: 8px !important; }
  .lp .mc-nav-signin { font-size: 13.5px !important; }
  .lp .mc-nav-cta { padding: 8px 12px !important; font-size: 13px !important; }
  .lp .mc-how-grid { gap: 28px !important; }
}

/* ===== Tablet — iPad portrait (641–1024px) ===== */
@media (min-width: 641px) and (max-width: 1024px) {
  .lp .mc-h1 { font-size: 62px !important; }
  .lp .mc-h2 { font-size: 40px !important; }
  .lp .mc-container { padding-left: 40px !important; padding-right: 40px !important; }
  .lp .mc-pad-section { padding-top: 84px !important; padding-bottom: 84px !important; }
  .lp .mc-gap64 { gap: 44px !important; }
}

/* ===== Laptop (1025–1440px) — default 1180px container ===== */

/* ===== Desktop / large (≥1441px) ===== */
@media (min-width: 1441px) {
  .lp .mc-container { max-width: 1320px !important; }
  .lp .mc-h1 { font-size: 88px !important; }
}
@media (min-width: 1700px) {
  .lp .mc-container { max-width: 1440px !important; }
}

/* Honor reduced-motion: hold the first hero item, disable keyframes + reveal transitions. */
@media (prefers-reduced-motion: reduce) {
  .lp * { animation: none !important; }
  .lp [data-reveal] { opacity: 1 !important; transform: none !important; transition: none !important; }
  .lp [data-stagger] > * { opacity: 1 !important; transform: none !important; transition: none !important; }
  .lp .mc-connectcard, .lp .mc-connectcard:hover { transition: none; transform: none; }
}
`
